import { getTemplateIframeDocument } from '@/components/template-builder/utils/helpers';
import {
  getCustomContactProperties,
  CustomContactSchema,
} from '@/configs/custom-contact-properties';
import {
  getCustomOrderProperties,
  getCustomCartProperties,
  getCustomProductProperties,
  CustomEntitySchema,
} from '@/configs/custom-entity-properties';
import { fetchCustomSchemas } from '@/composables/custom-data/customData';
// eslint-disable-next-line import/no-cycle
import { nestCall, NestApiType } from '@/composables/nestApi';
// eslint-disable-next-line import/no-cycle
import { eventsTypeEnum } from '@/types';
// eslint-disable-next-line import/no-cycle
import { OperatorType } from '@/types/generated-types/graphql';
import { Event } from '@/types/events-types';

export interface CollectionContext {
  collection: string;
  iterator: string;
  schema?: CustomContactSchema | CustomEntitySchema;
}

/**
 * Parse loop configuration from s-collection and s-item attributes
 */
function parseLoopConfiguration(element: HTMLElement): CollectionContext | null {
  const collectionAttr = element.getAttribute('s-collection');
  const itemAttr = element.getAttribute('s-item');

  if (!collectionAttr || !itemAttr) {
    return null;
  }

  return {
    collection: collectionAttr,
    iterator: itemAttr,
  };
}

/**
 * Get collection context from collection name
 * Returns the context type and schema if it's a custom collection
 */
export async function getCollectionSchema(collection: string): Promise<{
  context: 'contact' | 'order' | 'cart' | 'product' | 'visitor' | 'custom';
  schema?: CustomContactSchema | CustomEntitySchema;
}> {
  // Parse collection path (e.g., "contact.orders", "contact.custom.addresses", "products", "custom.veterinaires")
  const parts = collection.split('.');

  // Handle standalone custom collections (e.g., "custom.veterinaires")
  if (parts[0] === 'custom' && parts.length === 2) {
    const schemaName = parts[1];

    try {
      const allSchemas = await fetchCustomSchemas();
      const schema = allSchemas.find(
        (s) => s.schema_name.toLowerCase().replace(/\s+/g, '_') === schemaName,
      );

      if (schema) {
        // Convert to CustomEntitySchema format
        const customSchema: CustomEntitySchema = {
          schemaId: schema.id_custom_object?.toString() || '',
          schemaName: schema.schema_name || '',
          properties: schema.fields.map((field: any) => ({
            name: field.name,
            label: field.label || field.name,
            value: `${schema.id_custom_object}_${field.name}`,
            variableName: `${schemaName}.${field.name}`,
            type: field.type,
          })),
        };

        return { context: 'custom', schema: customSchema };
      }
    } catch (error) {
      console.error('Error loading standalone custom schema:', error);
    }

    return { context: 'custom' };
  }

  // Determine context from collection name
  let context: 'contact' | 'order' | 'cart' | 'product' | 'visitor';
  let schemaName: string | null = null;

  if (collection === 'products') {
    context = 'product';
    // Load all product schemas to show in variables
    try {
      const schemas = await getCustomProductProperties();
      // Return first schema if exists (products can have multiple schemas)
      if (schemas.length > 0) {
        return { context, schema: schemas[0] };
      }
    } catch (error) {
      console.error('Error loading product schemas:', error);
    }
    return { context };
  }

  context = parts[0] as 'contact' | 'order' | 'cart' | 'product' | 'visitor';

  // Check if it's a custom collection
  if (parts.includes('custom')) {
    schemaName = parts[parts.length - 1];

    try {
      // Load custom schemas based on context
      let schemas: (CustomContactSchema | CustomEntitySchema)[] = [];

      switch (context) {
        case 'contact':
          schemas = await getCustomContactProperties();
          break;
        case 'order':
          schemas = await getCustomOrderProperties();
          break;
        case 'cart':
          schemas = await getCustomCartProperties();
          break;
        case 'product':
          schemas = await getCustomProductProperties();
          break;
        case 'visitor':
        default:
          // No custom schemas for visitor context
          break;
      }

      // Find matching schema
      const schema = schemas.find(
        (s) => s.schemaName.toLowerCase().replace(/\s+/g, '_') === schemaName,
      );

      return { context, schema };
    } catch (error) {
      console.error('Error loading custom schema:', error);
      return { context };
    }
  }

  return { context };
}

/**
 * Detect all active collection contexts for a given element
 * Walks up the DOM tree to find all parent collections
 */
export async function detectCollectionContexts(
  selector: string,
): Promise<CollectionContext[]> {
  const template = getTemplateIframeDocument();
  if (!template) return [];

  const element = template.querySelector(selector);
  if (!element) return [];

  const loopConfigs: ReturnType<typeof parseLoopConfiguration>[] = [];
  let current = element as HTMLElement;

  // Walk up the DOM tree and collect all loop configurations
  while (current && current !== template.body) {
    const loopConfig = parseLoopConfiguration(current);
    if (loopConfig) {
      loopConfigs.push(loopConfig);
    }
    current = current.parentElement as HTMLElement;
  }

  // Load schemas for all collections in parallel
  const contextsPromises = loopConfigs.map(async (loopConfig) => {
    if (!loopConfig) return null;
    const { schema } = await getCollectionSchema(loopConfig.collection);
    return {
      collection: loopConfig.collection,
      iterator: loopConfig.iterator,
      schema,
    };
  });

  const results = await Promise.all(contextsPromises);
  return results.filter((ctx) => ctx !== null) as CollectionContext[];
}

/**
 * Get native properties for standard collections
 */
function getNativeCollectionProperties(collection: string, iterator: string): Record<string, unknown>[] {
  const propertiesMap: Record<string, string[]> = {
    // TODO : add native properties for standard collections after adding standard collections in getAvailableCollections
    // 'order.products': ['id', 'name', 'price', 'quantity', 'image_url', 'url'],
    // 'cart.products': ['id', 'name', 'price', 'quantity', 'image_url', 'url'],
  };

  const properties = propertiesMap[collection] || [];

  return properties.map((prop) => ({
    value: `{var=${iterator}.${prop}}`,
    label: `${iterator}.${prop}`,
    type: 'string',
    iterator,
    collection,
    isFromCollection: true,
  }));
}

/**
 * Get all available variables including collection contexts
 */
export async function getVariablesWithCollectionContext(
  selector: string,
  baseVariables: Record<string, unknown>[],
): Promise<Record<string, unknown>[]> {
  const contexts = await detectCollectionContexts(selector);

  if (contexts.length === 0) {
    return baseVariables;
  }

  const enrichedVariables = [...baseVariables];

  // Add variables for each collection context using forEach to avoid await in loop
  contexts.forEach((collectionContext) => {
    if (collectionContext.schema) {
      // Add custom schema properties with iteration badge (cyan)
      const schemaVariables = collectionContext.schema.properties.map((prop: any) => {
        // Extract relative path from variableName for nested relations
        // e.g., "contact.custom.magasins_contacts.magasins.name" -> "magasins.name"
        // e.g., "contact.custom.magasins_contacts.store_id" -> "store_id"
        let relativePath = prop.name;
        if (prop.variableName) {
          const parts = prop.variableName.split('.');
          // Find the schema table name in the path and take everything after it
          const schemaTableName = collectionContext.collection.split('.').pop();
          const schemaIndex = parts.findIndex((p: string) => p === schemaTableName);
          if (schemaIndex !== -1 && schemaIndex < parts.length - 1) {
            relativePath = parts.slice(schemaIndex + 1).join('.');
          }
        }

        return {
          value: `{var=${collectionContext.iterator}.${relativePath}}`,
          label: `${collectionContext.iterator}.${relativePath}`,
          labelWithBadge: `${collectionContext.iterator}.${relativePath} <span class="iteration-badge-inline">ITERATION</span>`,
          type: prop.type,
          iterator: collectionContext.iterator,
          collection: collectionContext.collection,
          isFromCollection: true,
          isCustom: true,
        };
      });

      enrichedVariables.push(...schemaVariables);
    } else {
      // Add native collection properties based on collection type
      const nativeProps = getNativeCollectionProperties(collectionContext.collection, collectionContext.iterator);
      enrichedVariables.push(...nativeProps);
    }
  });

  return enrichedVariables;
}

/**
 * Get variables enriched with a specific collection context (without DOM dependency)
 * Useful for code editors where we parse HTML directly
 * Only adds custom properties, native properties are already in baseVariables
 */
export async function getVariablesForCollection(
  collection: string,
  iterator: string,
  baseVariables: Record<string, unknown>[],
): Promise<Record<string, unknown>[]> {
  const enrichedVariables = [...baseVariables];

  // Get schema for this collection
  const { schema } = await getCollectionSchema(collection);

  if (schema) {
    // Add only custom schema properties with iteration badge (cyan)
    const schemaVariables = schema.properties.map((prop: any) => {
      // Extract relative path from variableName for nested relations
      let relativePath = prop.name;
      if (prop.variableName) {
        const parts = prop.variableName.split('.');
        // Find the schema table name in the path and take everything after it
        const schemaTableName = collection.split('.').pop();
        const schemaIndex = parts.findIndex((p: string) => p === schemaTableName);
        if (schemaIndex !== -1 && schemaIndex < parts.length - 1) {
          relativePath = parts.slice(schemaIndex + 1).join('.');
        }
      }

      return {
        value: `{var=${iterator}.${relativePath}}`,
        label: `${iterator}.${relativePath}`,
        labelWithBadge: `${iterator}.${relativePath} <span class="iteration-badge-inline">ITERATION</span>`,
        type: prop.type,
        iterator,
        collection,
        isFromCollection: true,
        isCustom: true,
      };
    });

    enrichedVariables.push(...schemaVariables);
  }

  return enrichedVariables;
}

/**
 * Get metadata variables from all custom events.
 * Returns variables in format: { value: '{var=metaData.<metadata_name>}', label: '<event_name> - <metadata_name>' }
 */
export async function getMetadataVariables(): Promise<Record<string, unknown>[]> {
  try {
    const params = {
      limit: 100,
      offset: 0,
      fetchAll: true,
      filters: [
        {
          field: 'type',
          value: eventsTypeEnum.EXTERNAL,
          operator: OperatorType.Equals,
        },
      ],
    };

    const result = await nestCall('get', NestApiType.CLIENT_API, '/events', {}, '', undefined, true, params);
    const events: Event[] = result?.data ?? [];

    const metadataVariables: Record<string, unknown>[] = [];

    events.forEach((event) => {
      const metadata = event.properties?.metaData;
      if (!metadata || !Array.isArray(metadata)) {
        return;
      }

      metadata.forEach((meta: { name: string; label?: string; type?: string }) => {
        metadataVariables.push({
          value: `{var=metaData.${meta.name}}`,
          label: `${event.name || event.code_name} - ${meta.label || meta.name}`,
          labelWithBadge: `${event.name || event.code_name} - ${meta.label || meta.name} <span class="metadata-badge-inline">METADATA</span>`,
          type: meta.type || 'text',
          isMetadata: true,
        });
      });
    });

    // Game metadata variables — always available
    metadataVariables.push(
      {
        value: '{var=metaData.game_prize_label}',
        label: 'Jeu - Nom du lot gagné',
        type: 'text',
      },
      {
        value: '{var=metaData.game_prize_image}',
        label: 'Jeu - Image du lot gagné',
        type: 'text',
      },
    );

    return metadataVariables;
  } catch (error) {
    console.error('Error fetching metadata variables:', error);
    return [];
  }
}
